// src/templateManager/TemplateManagerHome.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import {
  Container,
  Stack,
  Title,
  Text,
  Button,
  Group,
  Select,
  TextInput,
  Card,
  Badge,
  Grid,
  Loader,
  Alert
} from '@mantine/core';
import {
  IconPlus,
  IconSearch,
  IconRobot,
  IconBrain,
  IconSettings,
  IconCompass,
  IconGlobe,
  IconCopy,
  IconTrash,
  IconAlertCircle
} from '@tabler/icons-react';

/**
 * TemplateManagerHome - Lists all assessment templates
 * Allows filtering, creating, and managing templates
 */
function TemplateManagerHome() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [modelTypeFilter, setModelTypeFilter] = useState(null);
  const [publishedFilter, setPublishedFilter] = useState(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch('/.netlify/functions/templates-listTemplates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelType: modelTypeFilter,
          isPublished: publishedFilter
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to fetch templates');
      }

      setTemplates(result.templates || []);
    } catch (err) {
      console.error('[TemplateManagerHome] Error fetching templates:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [modelTypeFilter, publishedFilter]);

  // Fetch templates on mount
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Filter templates by search term
  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get icon for model type
  const getModelTypeIcon = (modelType) => {
    switch (modelType) {
      case 'generative':
        return <IconRobot size={24} />;
      case 'predictive':
        return <IconBrain size={24} />;
      case 'use_case':
        return <IconCompass size={24} />;
      case 'business_case':
        return <IconGlobe size={24} />;
      default:
        return <IconSettings size={24} />;
    }
  };

  // Get color for model type
  const getModelTypeColor = (modelType) => {
    switch (modelType) {
      case 'generative':
        return 'blue';
      case 'predictive':
        return 'green';
      case 'use_case':
        return 'orange';
      case 'business_case':
        return 'teal';
      default:
        return 'gray';
    }
  };

  const handleCreateTemplate = () => {
    navigate('/template-editor/new');
  };

  const handleEditTemplate = (templateId) => {
    navigate(`/template-editor/${templateId}`);
  };

  const handleCloneTemplate = async (template) => {
    // TODO: Implement cloning logic
    console.log('Clone template:', template);
  };

  const handleDeleteTemplate = async (templateId) => {
    // TODO: Implement delete with confirmation
    console.log('Delete template:', templateId);
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Assessment Templates</Title>
            <Text c="dimmed" size="sm">
              Manage taxonomy templates for different AI model types
            </Text>
          </div>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleCreateTemplate}
          >
            Create Template
          </Button>
        </Group>

        {/* Filters */}
        <Card withBorder p="md">
          <Grid>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <TextInput
                placeholder="Search templates..."
                leftSection={<IconSearch size={16} />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Select
                placeholder="Filter by model type"
                data={[
                  { value: 'all', label: 'All Types' },
                  { value: 'generative', label: 'Generative AI' },
                  { value: 'predictive', label: 'Predictive AI' },
                  { value: 'use_case', label: 'Navigator (Use Case)' },
                  { value: 'business_case', label: 'Explorer (Business Case)' },
                  { value: 'custom', label: 'Custom' }
                ]}
                value={modelTypeFilter || 'all'}
                onChange={(value) => setModelTypeFilter(value === 'all' ? null : value)}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Select
                placeholder="Filter by status"
                data={[
                  { value: 'all', label: 'All Status' },
                  { value: 'published', label: 'Published' },
                  { value: 'draft', label: 'Drafts' }
                ]}
                value={publishedFilter === true ? 'published' : publishedFilter === false ? 'draft' : 'all'}
                onChange={(value) => {
                  if (value === 'published') setPublishedFilter(true);
                  else if (value === 'draft') setPublishedFilter(false);
                  else setPublishedFilter(null);
                }}
                clearable
              />
            </Grid.Col>
          </Grid>
        </Card>

        {/* Error */}
        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        )}

        {/* Templates Grid */}
        {!loading && filteredTemplates.length === 0 && (
          <Card withBorder p="xl">
            <Stack align="center" gap="md">
              <IconSettings size={48} stroke={1.5} color="gray" />
              <Text c="dimmed">No templates found. Create your first template to get started.</Text>
              <Button leftSection={<IconPlus size={16} />} onClick={handleCreateTemplate}>
                Create Template
              </Button>
            </Stack>
          </Card>
        )}

        {!loading && filteredTemplates.length > 0 && (
          <Stack gap="md">
            {filteredTemplates.map((template) => (
              <Card key={template.id} withBorder p="lg" style={{ cursor: 'pointer' }}>
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Group>
                      {getModelTypeIcon(template.model_type)}
                      <div>
                        <Group gap="xs">
                          <Text fw={700} size="lg">{template.name}</Text>
                          {template.is_default && (
                            <Badge size="sm" color="yellow">Default</Badge>
                          )}
                          {template.is_published ? (
                            <Badge size="sm" color="green">Published</Badge>
                          ) : (
                            <Badge size="sm" color="gray">Draft</Badge>
                          )}
                        </Group>
                        <Group gap="xs" mt={4}>
                          <Badge size="sm" variant="light" color={getModelTypeColor(template.model_type)}>
                            {template.model_type}
                          </Badge>
                          <Text size="xs" c="dimmed">Version {template.version}</Text>
                          <Text size="xs" c="dimmed">•</Text>
                          <Text size="xs" c="dimmed">
                            Updated {new Date(template.updated_at).toLocaleDateString()}
                          </Text>
                        </Group>
                      </div>
                    </Group>
                    <Group gap="xs">
                      <Button
                        variant="light"
                        size="sm"
                        leftSection={<IconSettings size={16} />}
                        onClick={() => handleEditTemplate(template.id)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="subtle"
                        size="sm"
                        leftSection={<IconCopy size={16} />}
                        onClick={() => handleCloneTemplate(template)}
                      >
                        Clone
                      </Button>
                      <Button
                        variant="subtle"
                        color="red"
                        size="sm"
                        leftSection={<IconTrash size={16} />}
                        onClick={() => handleDeleteTemplate(template.id)}
                        disabled={template.is_default}
                      >
                        Delete
                      </Button>
                    </Group>
                  </Group>

                  <Text size="sm" c="dimmed">
                    {template.description}
                  </Text>
                </Stack>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}

export default TemplateManagerHome;
